import { Children, cloneElement, useEffect, useMemo, useState } from 'react'

import { IProperty, IPropertyPhoto } from '../../../../types-library/src'

import { useIntersectionObserver } from '@wiggot/hooks/src/useIntersectionObserver'

import Layout from './Layout'
import Arrows from './Arrows'
import { SwiperOptions } from 'swiper/types'
import PhotosCarousel from './PhotosCarousel'

interface Props {
  children?: React.ReactNode
  className?: string

  property?: IProperty
  swipeOptions?: SwiperOptions
  isCarouselDisabled?: boolean
  withBlurLoader?: boolean
  flyToMap?: boolean
  onPropertyClick?: (prop: IProperty) => void
  onChangePhotoIndex?: (index: number) => void
  onMouseEnter?: (property: IProperty) => void
  onMouseLeave?: (property: IProperty) => void
  onFlyToMap?: (property: IProperty) => void
}

export const PropertyCard = ({
  children,
  className,
  property,
  swipeOptions,
  isCarouselDisabled,
  flyToMap,
  onPropertyClick,
  onChangePhotoIndex,
  onFlyToMap,
  onMouseEnter,
  onMouseLeave,
}: Props): JSX.Element => {
  const { isVisible, observedElementRef, disconnect } =
    useIntersectionObserver<HTMLDivElement>({
      threshold: 0,
    })

  const [isCardHovered, setIsCardHovered] = useState(false)

  const HeaderElement = useMemo(() => {
    return Children.toArray(children).find(
      (child: any) => child.type === PropertyCard.Header
    )
  }, [children])

  const FooterElement = useMemo(() => {
    return Children.toArray(children).find(
      (child: any) => child.type === PropertyCard.Footer
    )
  }, [children])

  const photos = useMemo(() => {
    const photosArray=[]
  
    if(property?.coverImage){
      photosArray.push(property?.coverImage)
    }
    if(property?.photos?.length){
      photosArray.push(...property.photos)
    }
    if(photosArray.length && isCarouselDisabled){
      return [photosArray[0]]
    }
    return photosArray
  }, [property, isCarouselDisabled])

  useEffect(() => {
    if (flyToMap && property) {
      onFlyToMap?.(property)
    }
  }, [flyToMap])

  useEffect(() => {
    return () => {
      disconnect()
    }
  }, [])

  if (!property) {
    return <></>
  }

  const handlePropertyClick = () => {
    if (onPropertyClick && property) {
      onPropertyClick(property)
    }
  }
  return (
    <div ref={observedElementRef} className="w-full h-full property-card">
      <Layout
        className={className}
        property={property}
        onPropertyClick={handlePropertyClick}
        onMouseEnter={() => {
          onMouseEnter?.(property)
          setIsCardHovered(true)
        }}
        onMouseLeave={() => {
          onMouseLeave?.(property)
          setIsCardHovered(false)
        }}
      >
        <PhotosCarousel
          isVisible={isVisible}
          onChangeIndex={onChangePhotoIndex}
          photos={photos}
          swipeOptions={swipeOptions}
          isDisabled={isCarouselDisabled}
        >
          <div className="w-full h-full grid grid-cols-1 grid-rows-[max-content,auto,max-content]">
            <div className="w-full">
              {HeaderElement &&
                cloneElement(HeaderElement as React.ReactElement<HeaderProps>, {
                  property,
                  photos: property?.photos,
                })}
            </div>

            <div>
              {photos && photos.length > 1 && !isCarouselDisabled ? (
                <Arrows isCardHovered={isCardHovered} />
              ) : null}
            </div>

            <div className="w-full">
              {FooterElement &&
                cloneElement(FooterElement as React.ReactElement<FooterProps>, {
                  property,
                })}
            </div>
          </div>
        </PhotosCarousel>
      </Layout>
    </div>
  )
}

export default PropertyCard

interface HeaderProps {
  property?: IProperty
  photos?: IPropertyPhoto[]
  children: React.ReactNode
}

function Header({ property, photos, children }: HeaderProps) {
  return cloneElement(children as React.ReactElement<HeaderProps>, {
    property,
    photos,
  })
}

/**
 * React Node to render the property card header
 * */
PropertyCard.Header = Header

interface FooterProps {
  property?: IProperty
  children: React.ReactNode
}

function Footer({ property, children }: FooterProps) {
  return cloneElement(children as React.ReactElement<FooterProps>, { property })
}

/**
 * React Node to render the property card footer
 * */
PropertyCard.Footer = Footer
